import { useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import {
  isUsingTiptapEditor,
  isUsingTrixEditor,
} from "@circle-react/helpers/postHelpers";
import { supportWidgetPaths } from "@circle-react/helpers/urlHelpers";
import { DisplayTrixContent } from "@circle-react-uikit/DisplayTrixContent";
import { TipTapBlockEditor } from "@circle-react-uikit/TipTapBlockEditor";
import { Typography } from "@circle-react-uikit/Typography";
import { CIRCLE_KB_URL } from "../constants";
import { useShowPost } from "../hooks/useShowPost";
import { FeedbackSection } from "./FeedbackSection";
import { HelpWidgetLayout } from "./common/HelpWidgetLayout";
import { OpenInNewWindowLink } from "./common/OpenInNewWindowLink";
import { PostBreadcrumb } from "./common/PostBreadcrumb";
import { SkeletonLoader } from "./common/SkeletonLoader";

interface PostViewProps {
  id: string;
}

export const PostView = ({ id }: PostViewProps) => {
  const trixRef = useRef<HTMLElement | null>(null);
  const history = useHistory();
  const { isError, isLoading, data: post } = useShowPost(id);
  const urls = ["help-en.circle.so", "help-pt.circle.so", "pt-help.circle.so"];
  let postKbUrl = post?.post_url || CIRCLE_KB_URL;
  urls.forEach(url => {
    if (postKbUrl.includes(url)) {
      postKbUrl = postKbUrl.replace(url, "help.circle.so");
    }
  });

  const handleClick = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();

      const postId = event.currentTarget.getAttribute("data-post-id");
      history.push(supportWidgetPaths.showPost(postId));
    },
    [history],
  );
  const transformLinks = useCallback(
    ({ containerElement }) => {
      if (post) {
        containerElement
          .querySelectorAll(".trix-content a")
          .forEach((element: HTMLAnchorElement) => {
            const href = element.getAttribute("href");
            if (href && post.urls_hash[href]) {
              element.setAttribute("data-post-id", post.urls_hash[href]);
            } else {
              element.setAttribute("target", "_blank");
              element.setAttribute("rel", "noreferrer");
            }
          });

        containerElement
          .querySelectorAll(".trix-content a[data-post-id]")
          .forEach((element: HTMLAnchorElement) => {
            element.addEventListener("click", handleClick);
          });

        // Store ref to the Trix content, so we can remove event handlers later
        trixRef.current = containerElement;
      }
    },
    [post, handleClick],
  );

  // Remove click handlers
  useEffect(
    () => () => {
      if (trixRef.current) {
        trixRef.current
          .querySelectorAll(".trix-content a[data-post-id]")
          .forEach(element => {
            (element as HTMLAnchorElement).removeEventListener(
              "click",
              handleClick,
            );
          });
      }
    },
    [handleClick],
  );

  if (isLoading) {
    return (
      <HelpWidgetLayout className="flex flex-col gap-4">
        <SkeletonLoader
          rows={1}
          height="112px"
          wrapperClassName="mt-4 space-y-0"
          loaderClassName="rounded-2xl"
        />
        <SkeletonLoader rows={1} height="26px" loaderClassName="rounded-2xl" />
        <SkeletonLoader
          rows={1}
          width="310px"
          height="26px"
          loaderClassName="rounded-2xl"
        />
      </HelpWidgetLayout>
    );
  }

  if (isError) {
    return (
      <HelpWidgetLayout className="flex flex-col">
        <div className="support-widget-post__body grow">
          <div className="mx-6 my-3 max-h-full">
            {t("support_widget.post_not_found")}
          </div>
        </div>
      </HelpWidgetLayout>
    );
  }

  return (
    <HelpWidgetLayout className="flex h-full flex-col overflow-hidden">
      <div className="support-widget-post__body !mb-0 flex-1 overflow-y-auto">
        {post.cover_image_url && (
          <div>
            <img loading="lazy" alt="" src={post.cover_image_url} />
          </div>
        )}
        <div className="mx-5 my-4">
          <PostBreadcrumb
            spaceGroupName={post.space_group_name}
            spaceName={post.space_name}
            spaceId={post.space_id}
          />
        </div>
        <div className="mx-5 my-4">
          <Typography.TitleLg>
            <a
              className="!text-dark transition-colors duration-150 ease-linear hover:!text-[#4262F0]"
              href={postKbUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              {post.name}
            </a>
          </Typography.TitleLg>
        </div>
        <div className="mx-5 my-3 flex flex-col gap-2 pb-4">
          {isUsingTrixEditor(post) && post.body_trix_content && (
            <DisplayTrixContent
              content={post.body_trix_content}
              transformLinks={transformLinks}
            />
          )}
          {isUsingTiptapEditor(post) && post.tiptap_body && (
            <TipTapBlockEditor
              readOnly
              value={post.tiptap_body.body}
              sgidToObjectMap={post.tiptap_body.sgids_to_object_map}
              inlineAttachments={post.tiptap_body.inline_attachments}
            />
          )}
          <OpenInNewWindowLink version="full" postUrl={postKbUrl} />
          {post.space_slug !== "tutorials" && <FeedbackSection post={post} />}
        </div>
      </div>
    </HelpWidgetLayout>
  );
};
